/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { message as antMessage, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { getCandidateName } from '@sportninja/common/features/playerFinder/candidateDisplay'
import { PLAYER_FINDER_SUPPORT_URL } from '@sportninja/common/features/playerFinder/constants'
import { getSelectedCandidateNames } from '@sportninja/common/features/playerFinder/selectedCandidateNames'
import { sendableSelection } from '@sportninja/common/features/playerFinder/selection'
import {
  failedGroups,
  groupBySource,
  sendFailureMessage,
  type AvailabilityRequestGroup,
} from '@sportninja/common/features/playerFinder/sendGroups'
import type {
  SpareCandidate,
  SpareCandidateLocationFilter,
} from '@sportninja/common/features/playerFinder/types'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import BaseModal from 'src/components/ts-components/BaseModal'
import BetaBadge from 'src/components/ts-components/BetaBadge'
import Text from 'src/components/ts-components/Text'
import { useGameAttendancePlayers } from 'src/components/ManageTeamAttendanceModal/hooks/useGameAttendancePlayers'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import AvailabilityMessageModal from './components/AvailabilityMessageModal'
import PlayerFinderFilterBar from './components/PlayerFinderFilterBar'
import SelectionActionBar from './components/SelectionActionBar'
import SpareCandidateDetailModal from './components/SpareCandidateDetailModal'
import SpareCandidateList from './components/SpareCandidateList'
import { useAttendanceGameSchedule } from './hooks/useAttendanceGameSchedule'
import { usePlayerFinderSelection } from './hooks/usePlayerFinderSelection'
import { usePositionNameResolver } from './hooks/usePositionNameResolver'
import { useSendAvailabilityRequest } from './hooks/useSendAvailabilityRequest'
import { useSpareCandidates } from '@sportninja/common/features/playerFinder/hooks/useSpareCandidates'

// Above every modal layer (base wrapper + the deepest elevation step this
// feature uses), so the header's help tooltip paints over the modal instead of
// behind it. Derived from the shared tokens so it tracks any z-index change.
const TOOLTIP_Z_INDEX =
  MODAL_TOKENS.Z_INDEX_WRAPPER + MODAL_TOKENS.Z_INDEX_ELEVATION_STEP * 10

export interface PlayerFinderModalProps {
  isOpen: boolean
  onClose: () => void
  gameId: string
  rosterId: string
  teamId?: string
  sportId?: string
}

const PlayerFinderModal: React.FC<PlayerFinderModalProps> = ({
  isOpen,
  onClose,
  gameId,
  rosterId,
  teamId,
  sportId,
}) => {
  'use memo'
  const [positionId, setPositionId] = useState<string | undefined>(undefined)
  const [genderId, setGenderId] = useState<string | undefined>(undefined)
  // SN-8295 — where the players are: listed players by their card's
  // location, the team's own spares by their profile address.
  const [location, setLocation] = useState<SpareCandidateLocationFilter>({})
  // A typed city narrows the list once the typing pauses, not per keystroke.
  const debouncedCity = useDebounce(location.city ?? '', 400)
  // The send queued behind the message modal: the players to request, one
  // group per candidate source (SN-8309), and what to clean up once every group
  // has gone out.
  const [pendingSend, setPendingSend] = useState<{
    groups: AvailabilityRequestGroup[]
    // Resolved at queue time: the detail-card path can request a candidate that
    // a later filter change would drop from `candidates`, so the names can't be
    // derived from the list when the message modal renders.
    playerNames: string
    // Set once a send left groups unsent — `groups` is then only those, and the
    // modal's Retry resends just them.
    error: string | null
    onDone: () => void
  } | null>(null)
  // The queued send as last rendered: a send that fails reads it to tell whether
  // its message modal is still showing it.
  const pendingSendRef = useRef(pendingSend)
  useEffect(() => {
    pendingSendRef.current = pendingSend
  }, [pendingSend])
  // Dropping the queued send forgets it at once, not on the next render: a send
  // settling in between must see its modal as gone and report as a toast.
  const dropPendingSend = useCallback(() => {
    pendingSendRef.current = null
    setPendingSend(null)
  }, [])
  // Whether the finder is still mounted when a send settles: closing the
  // Attendance modal unmounts it.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // Set before a send starts, so a second click is refused even before the
  // re-render that shows the send in flight.
  const sendingRef = useRef(false)

  // The candidate whose detail modal is open (null when none).
  const [cardCandidate, setCardCandidate] = useState<SpareCandidate | null>(
    null
  )

  const selection = usePlayerFinderSelection()
  const { clear: clearSelection } = selection
  const {
    date,
    sportId: gameSportId,
    loading: gameLoading,
  } = useAttendanceGameSchedule(gameId)
  // The game record already carries sport_id, so the position filter/column and
  // the candidate's season stats scope to the right sport without an extra
  // request. An explicit prop still wins when the caller supplies one.
  const effectiveSportId = sportId ?? gameSportId
  const resolvePositionName = usePositionNameResolver(effectiveSportId)

  const { players: rosterPlayers, teamId: rosterTeamId } =
    useGameAttendancePlayers({ gameId, rosterId })
  const effectiveTeamId = teamId ?? rosterTeamId ?? undefined

  // SN-8309 — the server's eligibility list for this game and team, paged, less
  // anyone already asked or on this game's roster. The Attendance modal renders
  // this component unconditionally, so the hooks run even while closed; the
  // list is fetched only once the finder is open and the game (its venue-local
  // date) has loaded.
  const {
    candidates,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    nextPageFailed,
    fetchNextPage,
  } = useSpareCandidates(
    {
      gameId,
      teamId: effectiveTeamId,
      date,
      positionId,
      genderId,
      location: { ...location, city: debouncedCity },
    },
    {
      enabled: isOpen && !gameLoading,
      gameRosterPlayerIds: rosterPlayers.map((player) => player.id),
    }
  )
  const send = useSendAvailabilityRequest()
  // Who the send button reaches: selected and still listed.
  const sendableIds = sendableSelection(candidates, selection.selectedIds)

  // Both send paths queue the request and open the message modal first; the
  // POSTs fire from its onSend with the optional message.
  const openMessageModal = (
    groups: AvailabilityRequestGroup[],
    playerNames: string,
    onDone: () => void
  ) => {
    if (groups.length === 0) {
      return
    }
    setPendingSend({ groups, playerNames, error: null, onDone })
  }

  const onSendSelection = () => {
    openMessageModal(
      groupBySource(candidates, sendableIds),
      getSelectedCandidateNames(candidates, sendableIds),
      () => selection.clear()
    )
  }

  // Sends the queued groups — on a Retry, only the ones that failed. Never a
  // second send while one is out.
  const onSendMessage = (msg: string) => {
    if (!pendingSend || !gameId || !effectiveTeamId || sendingRef.current) {
      return
    }
    const queued = pendingSend
    sendingRef.current = true
    // The outcome is handled on the returned promise, not in mutate's
    // callbacks: those are skipped once the finder has unmounted, and a failure
    // must still be reported.
    send
      .mutateAsync({
        gameId,
        teamId: effectiveTeamId,
        groups: queued.groups,
        message: msg,
      })
      .then((outcomes) => {
        const error = sendFailureMessage(outcomes)
        if (error === null) {
          // Close ONLY the message modal — the finder and the Attendance modal
          // behind it stay open; the mutation's invalidations refresh them. If
          // the modal was closed while the asks were out, it stays closed and a
          // selection made since is kept.
          if (mountedRef.current && pendingSendRef.current === queued) {
            queued.onDone()
            setPendingSend(null)
          } else {
            // Only the players just asked drop out of the selection.
            selection.deselect(
              queued.groups.flatMap((group) => group.playerIds)
            )
          }
          antMessage.success('Your availability request has been sent.')
          return
        }
        // Keep the message modal up on the players not asked — unless it was
        // closed (or the finder closed, moved to another team or unmounted)
        // while the asks were out: then it stays closed and the error is a
        // toast. The asks that went out already show on the refreshed list.
        if (!mountedRef.current || pendingSendRef.current !== queued) {
          antMessage.error(error)
          return
        }
        const unsent = failedGroups(outcomes)
        setPendingSend((current) =>
          current === queued
            ? {
                ...queued,
                groups: unsent,
                playerNames: unsent
                  .flatMap((group) => group.playerNames)
                  .join(', '),
                error,
              }
            : current
        )
      })
      .finally(() => {
        sendingRef.current = false
      })
  }

  const onOpenCandidate = (candidate: SpareCandidate) => {
    setCardCandidate(candidate)
  }

  // The detail modal's request is for the OPEN player only — it deliberately
  // ignores the list's checkbox selection (matching mobile). Close the detail
  // modal first so only the message modal is up.
  const onSendFromCard = () => {
    if (!cardCandidate) {
      return
    }
    const candidate = cardCandidate
    setCardCandidate(null)
    openMessageModal(
      groupBySource([candidate], [candidate.id]),
      getCandidateName(candidate),
      () => {}
    )
  }

  // The Attendance modal renders this component unconditionally, so it stays
  // mounted for the life of the Attendance modal. Without this, closing the
  // finder without sending (the X) leaves the checkbox selection, the open
  // detail card, and any queued send sitting in state — reopening the finder
  // would then show a stale selection the user never re-confirmed. This must
  // NOT run on the successful-send path (which calls selection.clear() itself
  // and keeps the finder open) — it only fires when isOpen actually flips.
  useEffect(() => {
    if (!isOpen) {
      clearSelection()
      setCardCandidate(null)
      dropPendingSend()
    }
  }, [isOpen, clearSelection, dropPendingSend])

  // SN-8309 — the list is per game AND team, so a selection (or a queued send)
  // made for one team is dropped when the Attendance modal moves to another.
  useEffect(() => {
    clearSelection()
    setCardCandidate(null)
    dropPendingSend()
  }, [gameId, effectiveTeamId, clearSelection, dropPendingSend])

  if (!isOpen) {
    return null
  }

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        elevated
        desktopWidth='833px'
        title={
          <div css={titleRowStyle}>
            <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
              {capitalizeWords('Player Finder')}
            </Text>
            <BetaBadge />
            {/* Ant renders the tooltip in a portal at z-index ~1070, far below
                this elevated modal (10022+), so it would be painted behind the
                modal and never seen. TOOLTIP_Z_INDEX lifts it clear. */}
            <Tooltip
              title={capitalizeWords('Learn How Player Finder Works')}
              placement='right'
              zIndex={TOOLTIP_Z_INDEX}
            >
              <a
                css={helpIconStyle}
                href={PLAYER_FINDER_SUPPORT_URL}
                target='_blank'
                rel='noreferrer'
                aria-label='Player Finder help'
              >
                <QuestionCircleOutlined />
              </a>
            </Tooltip>
          </div>
        }
        actions={
          <SelectionActionBar
            count={sendableIds.length}
            sending={send.isPending}
            onSend={onSendSelection}
          />
        }
      >
        <div css={bodyStyle}>
          <PlayerFinderFilterBar
            sportId={effectiveSportId}
            positionId={positionId}
            genderId={genderId}
            location={location}
            onPositionChange={setPositionId}
            onGenderChange={setGenderId}
            onLocationChange={(patch) =>
              setLocation((current) => ({ ...current, ...patch }))
            }
          />
          <SpareCandidateList
            candidates={candidates}
            isLoading={isLoading}
            isError={isError}
            // A failed page stops automatic loading, so a failing server isn't
            // retried in a loop.
            hasNextPage={hasNextPage && !nextPageFailed}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            resolvePositionName={resolvePositionName}
            isSelected={selection.isSelected}
            onToggle={selection.toggle}
            onOpenCandidate={onOpenCandidate}
          />
        </div>
      </BaseModal>

      <AvailabilityMessageModal
        isOpen={Boolean(pendingSend)}
        sending={send.isPending}
        playerNames={pendingSend?.playerNames}
        error={pendingSend?.error}
        onSend={onSendMessage}
        onClose={dropPendingSend}
      />

      <SpareCandidateDetailModal
        candidate={cardCandidate}
        isOpen={Boolean(cardCandidate)}
        sending={send.isPending}
        resolvePositionName={resolvePositionName}
        sportId={effectiveSportId}
        onSend={onSendFromCard}
        onClose={() => setCardCandidate(null)}
      />
    </>
  )
}

export default PlayerFinderModal

const bodyStyle = css`
  display: flex;
  flex-direction: column;
`

/* Title + BETA badge + help "?" icon. Mirrors the attendance modal's title row; the right
   padding keeps the icon clear of BaseModal's close button. */
const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 32px;
`

const helpIconStyle = css`
  display: inline-flex;
  align-items: center;
  color: ${colors.PRIMARY};
  font-size: 18px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    color: ${colors.PRIMARY};
    opacity: 0.8;
  }
`
